import React from 'react';
import { ClientPortalLayout } from './ClientPortalLayout';
import { TargetIcon, BarChart2Icon, TrendingUpIcon, UsersIcon, ArrowUpRightIcon } from 'lucide-react';
import { motion } from 'framer-motion';
export function OutcomesDashboardPage() {
  const kpis = [{
    label: 'Strategic Alignment',
    value: '88',
    delta: '+42%',
    icon: TargetIcon,
    color: 'text-blue-500',
    bg: 'bg-blue-50'
  }, {
    label: 'AI Readiness Score',
    value: '76',
    delta: '+35%',
    icon: BarChart2Icon,
    color: 'text-purple-500',
    bg: 'bg-purple-50'
  }, {
    label: 'Decision Confidence',
    value: '92',
    delta: '+55%',
    icon: TrendingUpIcon,
    color: 'text-emerald-500',
    bg: 'bg-emerald-50'
  }, {
    label: 'Participant NPS',
    value: '84',
    delta: null,
    icon: UsersIcon,
    color: 'text-amber-500',
    bg: 'bg-amber-50'
  }];
  const shifts = [{
    label: 'Cross-functional Trust',
    start: 2.5,
    end: 4.2
  }, {
    label: 'Strategic Clarity',
    start: 3.0,
    end: 4.8
  }, {
    label: 'Decision Speed',
    start: 2.8,
    end: 4.1
  }, {
    label: 'Team Cohesion',
    start: 3.2,
    end: 4.5
  }];
  const nextSteps = [{
    title: 'Establish AI Governance Board',
    due: '30 Days',
    owner: 'CTO Office',
    color: 'bg-blue-100 text-blue-700'
  }, {
    title: 'Revise Q1 Investment Thesis',
    due: '15 Days',
    owner: 'Managing Partners',
    color: 'bg-purple-100 text-purple-700'
  }, {
    title: 'Cross-team Deal Review Process',
    due: '60 Days',
    owner: 'Operations',
    color: 'bg-emerald-100 text-emerald-700'
  }, {
    title: 'Commercial Integration Plan',
    due: '45 Days',
    owner: 'Commercial Team',
    color: 'bg-rose-100 text-rose-700'
  }];
  return <ClientPortalLayout data-id="element-505">
      <div className="p-6 lg:p-8 max-w-6xl mx-auto space-y-6" data-id="element-506">
        <div className="mb-8" data-id="element-507">
          <h1 className="font-serif-display text-3xl text-slate-900 mb-2" data-id="element-508">
            Outcomes Dashboard
          </h1>
          <p className="text-sm text-slate-500" data-id="element-509">
            Post-retreat metrics, shifts, and accountability tracking.
          </p>
        </div>

        {/* KPI Row */}
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4" data-id="element-510">
          {kpis.map((kpi, i) => <motion.div initial={{
          opacity: 0,
          y: 20
        }} animate={{
          opacity: 1,
          y: 0
        }} transition={{
          delay: i * 0.1
        }} key={i} className="bg-white rounded-2xl border border-slate-200 p-6 shadow-sm flex flex-col justify-between h-40" data-id="element-511">
              <div className="flex justify-between items-start" data-id="element-512">
                <div className={`w-10 h-10 rounded-xl ${kpi.bg} ${kpi.color} flex items-center justify-center`} data-id="element-513">
                  <kpi.icon className="w-5 h-5" data-id="element-514" />
                </div>
                {kpi.delta && <div className="flex items-center gap-1 px-2 py-1 bg-emerald-50 text-emerald-600 text-xs font-bold rounded-full" data-id="element-515">
                    <ArrowUpRightIcon className="w-3 h-3" data-id="element-516" />
                    {kpi.delta}
                  </div>}
              </div>
              <div data-id="element-517">
                <div className="text-sm text-slate-500 mb-1" data-id="element-518">{kpi.label}</div>
                <div className="flex items-baseline gap-1" data-id="element-519">
                  <span className="font-serif-display text-4xl text-slate-900" data-id="element-520">
                    {kpi.value}
                  </span>
                  {kpi.value !== '84' && <span className="text-lg text-slate-400 font-serif-display" data-id="element-521">
                      /100
                    </span>}
                </div>
              </div>
            </motion.div>)}
        </div>

        {/* 2-Col Grid */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6" data-id="element-522">
          {/* Shifts */}
          <div className="bg-white rounded-2xl border border-slate-200 p-8 shadow-sm" data-id="element-523">
            <h2 className="font-serif-display text-2xl text-slate-900 mb-8" data-id="element-524">
              Pre vs Post Retreat Shifts
            </h2>

            <div className="space-y-8" data-id="element-525">
              {shifts.map((shift, i) => <div key={i} data-id="element-526">
                  <div className="flex justify-between items-end mb-2" data-id="element-527">
                    <div className="text-sm font-bold text-slate-900" data-id="element-528">
                      {shift.label}
                    </div>
                    <div className="text-sm font-bold text-slate-900" data-id="element-529">
                      {shift.end} / 5.0
                    </div>
                  </div>
                  <div className="h-3 bg-slate-100 rounded-full overflow-hidden mb-2 relative" data-id="element-530">
                    <motion.div initial={{
                  width: 0
                }} animate={{
                  width: `${shift.end / 5 * 100}%`
                }} transition={{
                  duration: 1,
                  delay: 0.5
                }} className={`h-full rounded-full ${i % 2 === 0 ? 'bg-emerald-400' : 'bg-slate-700'}`} data-id="element-531" />
                  </div>
                  <div className="text-xs text-slate-400" data-id="element-532">
                    Started at {shift.start}
                  </div>
                </div>)}
            </div>
          </div>

          {/* Ownership Map */}
          <div className="bg-white rounded-2xl border border-slate-200 p-8 shadow-sm" data-id="element-533">
            <h2 className="font-serif-display text-2xl text-slate-900 mb-8" data-id="element-534">
              Next-Step Ownership Map
            </h2>

            <div className="space-y-4" data-id="element-535">
              {nextSteps.map((step, i) => <div key={i} className="p-4 rounded-xl border border-slate-100 bg-slate-50 flex items-center justify-between gap-4" data-id="element-536">
                  <div data-id="element-537">
                    <h3 className="font-bold text-slate-900 text-sm mb-1" data-id="element-538">
                      {step.title}
                    </h3>
                    <p className="text-xs text-slate-500" data-id="element-539">Due: {step.due}</p>
                  </div>
                  <span className={`px-3 py-1 rounded-full text-xs font-bold whitespace-nowrap ${step.color}`} data-id="element-540">
                    {step.owner}
                  </span>
                </div>)}
            </div>
          </div>
        </div>
      </div>
    </ClientPortalLayout>;
}